/**
 * The site's routes.
 *
 * `dealbid-web` is a static bundle served by Cloudflare with
 * `not_found_handling: single-page-application`, so every path resolves to the same shell
 * and the bundle decides what to render. Navigation between routes stays an ordinary
 * anchor: a real navigation keeps the back button, middle-click, "open in new tab" and
 * "copy link address" working without this repo re-implementing any of them, and a static
 * page has nothing to preserve across the load that is not already in `sessionStorage` or
 * `localStorage`.
 *
 * An unrecognised path renders the homepage rather than a 404 page. The board is the one
 * surface every visitor came for, and a visitor who mistypes a path is better served by it
 * than by a dead end.
 */

export const ROUTE_PATHS = {
  home: "/",
  /**
   * Retired (`WEB-014`). `WEB-003` hosted the submission form here; canon decision `D-008`
   * moved submission behind a Google sign-in and it now lives in `app.dealbid.app`.
   *
   * The path stays a *known* route rather than falling through to the homepage, because the
   * fallback below is silent: an old link would render the board and leave a founder to work
   * out for themselves that the form is gone. `SubmitRedirect` says where it went and takes
   * them there.
   */
  submit: "/submit",
  /** The Disclosure policy (`WEB-018`), the first of the static pages below. */
  disclosure: "/disclosure",
  /** The Privacy notice (`WEB-019`). */
  privacy: "/privacy",
} as const;

/**
 * The static pages, as route names (`WEB-018`).
 *
 * `docs/ARCHITECTURE.md` § Public web puts "legal/static pages" in this repo. They are all
 * the same kind of thing — a prose document under the ordinary chrome, no data, no state —
 * so they share one layout (`src/sections/StaticPage.tsx`) and one entry in this table
 * rather than each bringing a route branch of its own.
 *
 * Adding one is a `ROUTE_PATHS` entry, a name here and a document component. `WEB-019` adds
 * Terms and Privacy that way; nothing in the routing has to be reworked to take them.
 *
 * The order is the order the footer lists them in, so the two cannot drift apart.
 */
export const STATIC_PAGES = ["disclosure", "privacy"] as const;

/**
 * The homepage's sections, as link targets (canon `D-007`).
 *
 * Written as a full path plus fragment rather than a bare `#id` because the masthead
 * carries them on every page: from the retired `/submit` a bare fragment names nothing and
 * the link goes nowhere.
 *
 * `D-006` briefly moved four of these onto an `/about` route and `D-007` brought them back,
 * so each id is the one it has always had — `#what-is-dealbid` and its siblings resolve
 * against `/` exactly as they did before either decision.
 */
export const HOME_SECTIONS = {
  today: `${ROUTE_PATHS.home}#today`,
  honorable: `${ROUTE_PATHS.home}#honorable`,
  /**
   * The click-ranked section itself, and each of its two lists.
   *
   * `byClicks` is what the masthead points at (`WEB-033`). With both periods on the page
   * under one heading, a nav item that named `#weekly` would land a reader *past* the
   * section's own title and past core's disclosure sentence — which canon § 12 requires be
   * visible where the ranking is. `#weekly` and `#monthly` remain, because `D-007`
   * established them and every decision since has kept them resolving: they are what an
   * external or saved link names, and each lands on its own list's heading.
   */
  byClicks: `${ROUTE_PATHS.home}#by-clicks`,
  weekly: `${ROUTE_PATHS.home}#weekly`,
  monthly: `${ROUTE_PATHS.home}#monthly`,
  whatIsDealbid: `${ROUTE_PATHS.home}#what-is-dealbid`,
  tomorrow: `${ROUTE_PATHS.home}#tomorrow`,
  howItWorks: `${ROUTE_PATHS.home}#how-it-works`,
  /** The permanent Founding cohort (`WEB-015`, canon decision `D-009`). */
  founding: `${ROUTE_PATHS.home}#founding`,
  subscribe: `${ROUTE_PATHS.home}#subscribe`,
} as const;

export type RouteName = keyof typeof ROUTE_PATHS;

/** A route that renders a static prose document rather than the board. */
export type StaticPageName = (typeof STATIC_PAGES)[number];

/**
 * Every non-home route, longest path first.
 *
 * Matching runs off the table rather than off a chain of comparisons so that a new static
 * page is routed by declaring it, not by remembering to extend a branch here. Longest
 * first keeps a nested path (`/terms/eu`, say) from being claimed by its prefix.
 */
const MATCHABLE: readonly (readonly [RouteName, string])[] = (
  Object.entries(ROUTE_PATHS) as [RouteName, string][]
)
  .filter(([name]) => name !== "home")
  .sort((a, b) => b[1].length - a[1].length);

/** Matches a pathname to a route, tolerating a trailing slash. */
export function routeForPath(pathname: string): RouteName {
  const normalized = pathname.replace(/\/+$/, "").toLowerCase();
  const match = MATCHABLE.find(([, path]) => path === normalized);
  return match ? match[0] : "home";
}

/** Whether a route renders one of the static prose documents. */
export function isStaticPage(route: RouteName): route is StaticPageName {
  return (STATIC_PAGES as readonly string[]).includes(route);
}

/** The route the browser is currently on. */
export function currentRoute(): RouteName {
  return routeForPath(window.location.pathname);
}
